import { useMemo } from 'react';
import { useT } from '../i18n';
import { SOCIAL_LABELS, socialLinks, type SocialNetwork } from './social';

/**
 * ODKAZY NA SOCIÁLNÍ SÍTĚ V PATIČCE (#31).
 *
 * Když není nastavená ani jedna, blok se nevykreslí — prázdný pruh ikon
 * vypadá jako rozbitý obchod. Kde se adresy berou a proč, stojí v `social.ts`.
 *
 * Ikony jsou vložené SVG. Ikonový font ani obrázek z CDN by znamenal soubor
 * navíc a přenos k cizímu serveru na každé stránce — přesně to, co obchod
 * u sociálních sítí nechce.
 *
 * Vzhled si bere `.footnav` z patičky (řádek odkazů, tlumená barva, akcent
 * při najetí). Ikona kreslí `currentColor`, takže se chová stejně jako text
 * vedle ní a stylopis se kvůli tomuhle bloku měnit nemusel.
 */
export function SocialLinks() {
  const { t } = useT();
  const links = useMemo(socialLinks, []);

  if (links.length === 0) return null;

  return (
    <nav className="footnav social-links" aria-label={t('social.heading')}>
      {links.map(({ network, url }) => (
        <a
          key={network}
          className="social-link"
          href={url}
          // Cizí web nesmí dostat `window.opener` naší stránky.
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('social.follow', { network: SOCIAL_LABELS[network] })}
          title={SOCIAL_LABELS[network]}
        >
          <SocialIcon network={network} />
        </a>
      ))}
    </nav>
  );
}

/** Značkové ikony, kreslené. Bez písmen — název nese `aria-label`. */
function SocialIcon({ network }: { network: SocialNetwork }) {
  const common = {
    viewBox: '0 0 24 24',
    width: 20,
    height: 20,
    'aria-hidden': true,
    focusable: 'false' as const,
    fill: 'currentColor',
  };

  switch (network) {
    case 'facebook':
      return (
        <svg {...common}>
          <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H16.7V3.6c-.3-.04-1.3-.13-2.46-.13-2.44 0-4.1 1.49-4.1 4.22V9.9H7.4V13h2.74v8z" />
        </svg>
      );
    case 'instagram':
      return (
        <svg {...common}>
          <path d="M12 2.9c2.96 0 3.31.01 4.48.06 1.08.05 1.67.23 2.06.38.52.2.89.44 1.28.83.39.39.63.76.83 1.28.15.39.33.98.38 2.06.05 1.17.06 1.52.06 4.48s-.01 3.31-.06 4.48c-.05 1.08-.23 1.67-.38 2.06-.2.52-.44.89-.83 1.28-.39.39-.76.63-1.28.83-.39.15-.98.33-2.06.38-1.17.05-1.52.06-4.48.06s-3.31-.01-4.48-.06c-1.08-.05-1.67-.23-2.06-.38-.52-.2-.89-.44-1.28-.83-.39-.39-.63-.76-.83-1.28-.15-.39-.33-.98-.38-2.06C2.91 15.31 2.9 14.96 2.9 12s.01-3.31.06-4.48c.05-1.08.23-1.67.38-2.06.2-.52.44-.89.83-1.28.39-.39.76-.63 1.28-.83.39-.15.98-.33 2.06-.38C8.69 2.91 9.04 2.9 12 2.9m0 3.7A5.4 5.4 0 1 0 17.4 12 5.4 5.4 0 0 0 12 6.6m0 8.9A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5m5.6-9a1.26 1.26 0 1 0 1.26 1.26A1.26 1.26 0 0 0 17.6 6.5" />
        </svg>
      );
    case 'youtube':
      return (
        <svg {...common}>
          <path d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.28 5 12 5 12 5s-6.28 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.72 19 12 19 12 19s6.28 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8M10 15.1V8.9l5.2 3.1z" />
        </svg>
      );
    case 'x':
      return (
        <svg {...common}>
          <path d="M17.5 3h3.1l-6.77 7.74L21.8 21h-6.23l-4.88-6.38L5.11 21H2l7.24-8.28L2.5 3h6.39l4.41 5.83zm-1.09 16.15h1.72L7.67 4.76H5.83z" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg {...common}>
          <path d="M16.6 2h-3.1v13.2a2.6 2.6 0 1 1-2.2-2.57V9.5a5.7 5.7 0 1 0 5.3 5.69V8.9a6.4 6.4 0 0 0 3.9 1.32V7.1a3.5 3.5 0 0 1-3.9-3.5z" />
        </svg>
      );
  }
}
